import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { ShieldCheck, BarChart, Megaphone, Sparkles } from "lucide-react"

export default function ManagePreferencesModal({ isOpen, onClose, onSavePreferences }) {
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    functional: false,
  })

  useEffect(() => {
    if (isOpen) {
      const storedPreferences = localStorage.getItem("cookie_preferences")
      setPreferences(
        storedPreferences
          ? JSON.parse(storedPreferences)
          : { essential: true, analytics: false, marketing: false, functional: false }
      )
    }
  }, [isOpen])

  const handleToggle = (category) => {
    setPreferences((prev) => ({ ...prev, [category]: !prev[category] }))
  }

  const handleSave = () => {
    localStorage.setItem("cookie_preferences", JSON.stringify(preferences))
    onSavePreferences(preferences)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] rounded-2xl shadow-xl p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Manage Cookie Preferences</DialogTitle>
          <DialogDescription>
            Choose which types of cookies you'd like us to use. You can change your preferences anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-4">
          {/* Essential */}
          <div className="flex items-center justify-between bg-muted/10 p-4 rounded-xl">
            <Label htmlFor="essential-cookies" className="flex items-start gap-3">
              <ShieldCheck className="text-primary mt-1" />
              <div>
                <span className="font-medium">Essential Cookies</span>
                <p className="text-sm text-muted-foreground">Required for basic site functionality.</p>
              </div>
            </Label>
            <Switch id="essential-cookies" checked={true} disabled />
          </div>

          {/* Analytics */}
          <div className="flex items-center justify-between bg-muted/10 p-4 rounded-xl">
            <Label htmlFor="analytics-cookies" className="flex items-start gap-3">
              <BarChart className="text-primary mt-1" />
              <div>
                <span className="font-medium">Analytics Cookies</span>
                <p className="text-sm text-muted-foreground">Help us improve by collecting usage data.</p>
              </div>
            </Label>
            <Switch
              id="analytics-cookies"
              checked={preferences.analytics}
              onCheckedChange={() => handleToggle("analytics")}
            />
          </div>

          {/* Marketing */}
          <div className="flex items-center justify-between bg-muted/10 p-4 rounded-xl">
            <Label htmlFor="marketing-cookies" className="flex items-start gap-3">
              <Megaphone className="text-primary mt-1" />
              <div>
                <span className="font-medium">Marketing Cookies</span>
                <p className="text-sm text-muted-foreground">Used to show relevant advertisements.</p>
              </div>
            </Label>
            <Switch
              id="marketing-cookies"
              checked={preferences.marketing}
              onCheckedChange={() => handleToggle("marketing")}
            />
          </div>

          {/* Functional */}
          <div className="flex items-center justify-between bg-muted/10 p-4 rounded-xl">
            <Label htmlFor="functional-cookies" className="flex items-start gap-3">
              <Sparkles className="text-primary mt-1" />
              <div>
                <span className="font-medium">Functional Cookies</span>
                <p className="text-sm text-muted-foreground">Enable enhanced features and personalization.</p>
              </div>
            </Label>
            <Switch
              id="functional-cookies"
              checked={preferences.functional}
              onCheckedChange={() => handleToggle("functional")}
            />
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save Preferences</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
